import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { exportWhatIfScenariosPDF } from '../../../utils/scenarioExportService';

const WhatIfScenarioManager = ({ isOpen, onClose, baseConfig, baseCalculations }) => {
  const [scenarios, setScenarios] = useState([
    {
      id: 'base',
      name: 'Base Scenario',
      deliveryMode: baseConfig?.deliveryMode,
      config: baseConfig?.config,
      selectedEngineers: baseConfig?.selectedEngineers,
      isBase: true
    }
  ]);
  const [editingScenario, setEditingScenario] = useState(null);

  const deliveryModeMultipliers = {
    onsite: 1.0,
    remote: 0.85,
    hybrid: 0.92
  };

  const calculateScenario = (scenario) => {
    const modeMultiplier = deliveryModeMultipliers?.[scenario?.deliveryMode];
    const productiveHoursPerMonth = 160;

    const totalEngineerCosts = scenario?.selectedEngineers?.reduce((sum, eng) => {
      return sum + (eng?.count * eng?.hours * eng?.costPerHour * modeMultiplier);
    }, 0);

    const totalMaterialsCost = baseConfig?.selectedMaterials?.reduce((sum, mat) => {
      const baseCost = mat?.quantity * mat?.unitCost;
      const wasteCost = baseCost * (mat?.wastePercentage / 100);
      return sum + baseCost + wasteCost;
    }, 0);

    const totalLicensesCosts = baseConfig?.selectedLicenses?.reduce((sum, lic) => {
      if (lic?.type === 'perpetual') {
        return sum + (lic?.oneTimeCost * lic?.quantity);
      }
      return sum + (lic?.monthlyCost * lic?.quantity * lic?.duration);
    }, 0);

    const directCosts = totalEngineerCosts + totalMaterialsCost + totalLicensesCosts;
    const overheadCosts = directCosts * (scenario?.config?.overhead / 100);
    const contingencyCosts = (directCosts + overheadCosts) * (scenario?.config?.contingency / 100);
    const totalCost = directCosts + overheadCosts + contingencyCosts;
    const sellPrice = totalCost / (1 - scenario?.config?.targetMargin / 100);
    const grossProfit = sellPrice - totalCost;
    const profitMarginPercentage = (grossProfit / sellPrice) * 100;

    return {
      totalEngineerCosts,
      totalMaterialsCost,
      totalLicensesCosts,
      directCosts,
      overheadCosts,
      contingencyCosts,
      totalCost,
      sellPrice,
      grossProfit,
      profitMarginPercentage
    };
  };

  const scenarioCalculations = useMemo(() => {
    return scenarios?.map(scenario => ({
      ...scenario,
      calculations: calculateScenario(scenario)
    }));
  }, [scenarios, baseConfig]);

  const handleAddScenario = () => {
    const newScenario = {
      id: `scenario-${Date.now()}`,
      name: `Scenario ${scenarios?.length}`,
      deliveryMode: baseConfig?.deliveryMode,
      config: { ...baseConfig?.config },
      selectedEngineers: baseConfig?.selectedEngineers?.map(eng => ({ ...eng })),
      isBase: false
    };
    setScenarios([...scenarios, newScenario]);
    setEditingScenario(newScenario?.id);
  };

  const handleDeleteScenario = (scenarioId) => {
    setScenarios(scenarios?.filter(s => s?.id !== scenarioId));
    if (editingScenario === scenarioId) {
      setEditingScenario(null);
    }
  };

  const handleUpdateScenario = (scenarioId, updates) => {
    setScenarios(scenarios?.map(s => 
      s?.id === scenarioId ? { ...s, ...updates } : s
    ));
  };

  const handleUpdateEngineerCount = (scenarioId, engineerIndex, newCount) => {
    setScenarios(scenarios?.map(s => {
      if (s?.id === scenarioId) {
        const updatedEngineers = [...s?.selectedEngineers];
        updatedEngineers[engineerIndex] = {
          ...updatedEngineers?.[engineerIndex],
          count: parseInt(newCount) || 0
        };
        return { ...s, selectedEngineers: updatedEngineers };
      }
      return s;
    }));
  };

  const calculateVariance = (scenarioValue, baseValue) => {
    if (!baseValue) return 0;
    return ((scenarioValue - baseValue) / baseValue) * 100;
  };

  const formatCurrency = (value) => {
    return `OMR ${parseFloat(value || 0)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
  };

  const formatPercentage = (value) => {
    return `${value?.toFixed(2)}%`;
  };

  const getVarianceColor = (variance) => {
    if (variance > 5) return 'text-error';
    if (variance < -5) return 'text-success';
    return 'text-warning';
  };

  const getImpactIndicator = (variance) => {
    const absVariance = Math.abs(variance);
    if (absVariance > 15) return { label: 'High Impact', color: 'bg-error', icon: 'AlertTriangle' };
    if (absVariance > 5) return { label: 'Medium Impact', color: 'bg-warning', icon: 'AlertCircle' };
    return { label: 'Low Impact', color: 'bg-success', icon: 'CheckCircle2' };
  };

  const handleExportPDF = () => {
    exportWhatIfScenariosPDF(scenarioCalculations, baseConfig);
  };

  if (!isOpen) return null;

  const baseScenarioCalc = scenarioCalculations?.find(s => s?.isBase)?.calculations;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border bg-gradient-to-r from-primary/10 to-accent/10">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent shadow-lg">
              <Icon name="GitBranch" size={24} color="white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">What-If Scenario Analysis</h2>
              <p className="text-sm text-muted-foreground mt-1">Test cost impacts of delivery mode, resource, and contingency changes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {scenarios?.length} scenario{scenarios?.length !== 1 ? 's' : ''} created
            </div>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddScenario}
              disabled={scenarios?.length >= 5}
            >
              Add Scenario
            </Button>
          </div>

          {scenarios?.length >= 5 && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 flex items-start gap-2">
              <Icon name="Info" size={16} color="var(--color-warning)" />
              <p className="text-xs text-warning">Maximum 5 scenarios allowed for optimal comparison</p>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6">
            {scenarioCalculations?.map((scenario, index) => {
              const isEditing = editingScenario === scenario?.id;
              const variance = scenario?.isBase ? 0 : calculateVariance(scenario?.calculations?.totalCost, baseScenarioCalc?.totalCost);
              const impact = getImpactIndicator(variance);

              return (
                <div
                  key={scenario?.id}
                  className={`border-2 rounded-xl p-5 transition-all ${
                    scenario?.isBase
                      ? 'border-primary/30 bg-primary/5'
                      : isEditing
                      ? 'border-accent/50 bg-accent/5 shadow-lg'
                      : 'border-border bg-card hover:border-primary/20'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3 flex-1">
                      {scenario?.isBase ? (
                        <div className="px-3 py-1 bg-primary/20 text-primary text-xs font-semibold rounded-full">
                          BASE
                        </div>
                      ) : (
                        <div className={`px-3 py-1 ${impact?.color}/20 text-${impact?.color?.replace('bg-', '')} text-xs font-semibold rounded-full flex items-center gap-1`}>
                          <Icon name={impact?.icon} size={12} />
                          {impact?.label}
                        </div>
                      )}
                      <Input
                        value={scenario?.name}
                        onChange={(e) => handleUpdateScenario(scenario?.id, { name: e?.target?.value })}
                        className="text-base font-semibold max-w-xs"
                        disabled={scenario?.isBase}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {!scenario?.isBase && (
                        <>
                          <button
                            onClick={() => setEditingScenario(isEditing ? null : scenario?.id)}
                            className="p-2 hover:bg-muted rounded-lg transition-smooth"
                          >
                            <Icon name={isEditing ? "Check" : "Edit2"} size={16} color="var(--color-primary)" />
                          </button>
                          <button
                            onClick={() => handleDeleteScenario(scenario?.id)}
                            className="p-2 hover:bg-error/10 rounded-lg transition-smooth"
                          >
                            <Icon name="Trash2" size={16} color="var(--color-error)" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">Delivery Mode</label>
                      <Select
                        value={scenario?.deliveryMode}
                        onChange={(e) => handleUpdateScenario(scenario?.id, { deliveryMode: e?.target?.value })}
                        disabled={scenario?.isBase || !isEditing}
                        className="text-sm"
                      >
                        <option value="onsite">On-site (1.0x)</option>
                        <option value="hybrid">Hybrid (0.92x)</option>
                        <option value="remote">Remote (0.85x)</option>
                      </Select>
                    </div>

                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">Overhead (%)</label>
                      <Input
                        type="number"
                        value={scenario?.config?.overhead}
                        onChange={(e) => handleUpdateScenario(scenario?.id, {
                          config: { ...scenario?.config, overhead: parseFloat(e?.target?.value) || 0 }
                        })}
                        disabled={scenario?.isBase || !isEditing}
                        min="0"
                        max="100"
                        step="0.5"
                        className="text-sm"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">Target Margin (%)</label>
                      <Input
                        type="number"
                        value={scenario?.config?.targetMargin}
                        onChange={(e) => handleUpdateScenario(scenario?.id, {
                          config: { ...scenario?.config, targetMargin: parseFloat(e?.target?.value) || 0 }
                        })}
                        disabled={scenario?.isBase || !isEditing}
                        min="0"
                        max="100"
                        step="0.5"
                        className="text-sm"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">Contingency (%)</label>
                      <Input
                        type="number"
                        value={scenario?.config?.contingency}
                        onChange={(e) => handleUpdateScenario(scenario?.id, {
                          config: { ...scenario?.config, contingency: parseFloat(e?.target?.value) || 0 }
                        })}
                        disabled={scenario?.isBase || !isEditing}
                        min="0"
                        max="100"
                        step="0.5"
                        className="text-sm"
                      />
                    </div>
                  </div>

                  {isEditing && !scenario?.isBase && (
                    <div className="mb-4 p-4 bg-muted/30 rounded-lg">
                      <div className="text-xs font-semibold text-foreground mb-3">Resource Adjustments</div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {scenario?.selectedEngineers?.map((eng, engIndex) => (
                          <div key={engIndex} className="flex items-center gap-2">
                            <div className="flex-1">
                              <div className="text-xs text-muted-foreground truncate">{eng?.role}</div>
                              <div className="text-xs font-medium text-foreground">{eng?.hours}h @ OMR {eng?.costPerHour}/h</div>
                            </div>
                            <Input
                              type="number"
                              value={eng?.count}
                              onChange={(e) => handleUpdateEngineerCount(scenario?.id, engIndex, e?.target?.value)}
                              min="0"
                              max="50"
                              className="w-20 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted/20 rounded-lg">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Total Cost</div>
                      <div className="text-base font-bold text-foreground">
                        {formatCurrency(scenario?.calculations?.totalCost)}
                      </div>
                      {!scenario?.isBase && (
                        <div className={`text-xs font-semibold mt-1 ${getVarianceColor(variance)}`}>
                          {variance > 0 ? '+' : ''}{formatPercentage(variance)}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Sell Price</div>
                      <div className="text-base font-bold text-primary">
                        {formatCurrency(scenario?.calculations?.sellPrice)}
                      </div>
                      {!scenario?.isBase && (
                        <div className={`text-xs font-semibold mt-1 ${getVarianceColor(calculateVariance(scenario?.calculations?.sellPrice, baseScenarioCalc?.sellPrice))}`}>
                          {calculateVariance(scenario?.calculations?.sellPrice, baseScenarioCalc?.sellPrice) > 0 ? '+' : ''}
                          {formatPercentage(calculateVariance(scenario?.calculations?.sellPrice, baseScenarioCalc?.sellPrice))}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Gross Profit</div>
                      <div className="text-base font-bold text-success">
                        {formatCurrency(scenario?.calculations?.grossProfit)}
                      </div>
                      {!scenario?.isBase && (
                        <div className={`text-xs font-semibold mt-1 ${getVarianceColor(calculateVariance(scenario?.calculations?.grossProfit, baseScenarioCalc?.grossProfit) * -1)}`}>
                          {calculateVariance(scenario?.calculations?.grossProfit, baseScenarioCalc?.grossProfit) > 0 ? '+' : ''}
                          {formatPercentage(calculateVariance(scenario?.calculations?.grossProfit, baseScenarioCalc?.grossProfit))}
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="text-xs text-muted-foreground mb-1">Profit Margin</div>
                      <div className="text-base font-bold text-accent">
                        {formatPercentage(scenario?.calculations?.profitMarginPercentage)}
                      </div>
                      {!scenario?.isBase && (
                        <div className={`text-xs font-semibold mt-1 ${getVarianceColor(calculateVariance(scenario?.calculations?.profitMarginPercentage, baseScenarioCalc?.profitMarginPercentage) * -1)}`}>
                          {(scenario?.calculations?.profitMarginPercentage - baseScenarioCalc?.profitMarginPercentage) > 0 ? '+' : ''}
                          {formatPercentage(scenario?.calculations?.profitMarginPercentage - baseScenarioCalc?.profitMarginPercentage)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {scenarios?.length > 1 && (
            <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border border-primary/20 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
                <h3 className="text-base font-semibold text-foreground">Scenario Insights</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-card rounded-lg p-4">
                  <div className="text-xs text-muted-foreground mb-2">Cost Range</div>
                  <div className="text-sm font-bold text-foreground">
                    {formatCurrency(Math.min(...scenarioCalculations?.map(s => s?.calculations?.totalCost)))}
                    {' - '}
                    {formatCurrency(Math.max(...scenarioCalculations?.map(s => s?.calculations?.totalCost)))}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Variance: {formatCurrency(Math.max(...scenarioCalculations?.map(s => s?.calculations?.totalCost)) - Math.min(...scenarioCalculations?.map(s => s?.calculations?.totalCost)))}
                  </div>
                </div>

                <div className="bg-card rounded-lg p-4">
                  <div className="text-xs text-muted-foreground mb-2">Best Profit Margin</div>
                  <div className="text-sm font-bold text-success">
                    {formatPercentage(Math.max(...scenarioCalculations?.map(s => s?.calculations?.profitMarginPercentage)))}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {scenarioCalculations?.find(s => s?.calculations?.profitMarginPercentage === Math.max(...scenarioCalculations?.map(sc => sc?.calculations?.profitMarginPercentage)))?.name}
                  </div>
                </div>

                <div className="bg-card rounded-lg p-4">
                  <div className="text-xs text-muted-foreground mb-2">Lowest Cost</div>
                  <div className="text-sm font-bold text-primary">
                    {formatCurrency(Math.min(...scenarioCalculations?.map(s => s?.calculations?.totalCost)))}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {scenarioCalculations?.find(s => s?.calculations?.totalCost === Math.min(...scenarioCalculations?.map(sc => sc?.calculations?.totalCost)))?.name}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={handleExportPDF}
          >
            Export PDF Report
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WhatIfScenarioManager;